import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import PersonRemoveRoundedIcon from "@mui/icons-material/PersonRemoveRounded";
import { useTranslation } from "react-i18next";
import { apifacilGroupAdmin, getApifacilGroupParticipants } from "../../api/client";
import WhatsappGroupParticipantsPicker from "../clients/WhatsappGroupParticipantsPicker";
import { MIN_CLIENT_SEARCH_LENGTH, useClients } from "../../hooks/useClients";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { clientWhatsappPhoneDigits, formatClientDisplayPhone } from "../../utils/phone";

function readActionError(err, fallback) {
  const raw =
    (typeof err?.message === "string" && err.message.trim()) ||
    (typeof err?.data?.message === "string" && err.data.message.trim()) ||
    "";
  return raw || fallback;
}

/** Modal com as ações que a OpenAPI v2 documenta: listar, incluir, remover e sair. Sem apagar grupo. */
export default function WhatsappGroupManageDialog({
  open,
  onClose,
  channelId,
  groupJid,
  projectId,
}) {
  const { t } = useTranslation("chat");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [toAdd, setToAdd] = useState([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);
  const { data: clientsData, isLoading: loadingClients } = useClients(
    1,
    15,
    debouncedSearch.trim(),
    projectId || undefined,
    {
      enabled:
        !!open &&
        typeof debouncedSearch === "string" &&
        debouncedSearch.trim().length >= MIN_CLIENT_SEARCH_LENGTH,
    }
  );

  const loadMembers = async ({ clearError = true } = {}) => {
    if (!channelId || !groupJid) return;
    setLoading(true);
    if (clearError) setError("");
    try {
      const data = await getApifacilGroupParticipants(channelId, groupJid);
      setMembers(Array.isArray(data?.participants) ? data.participants : []);
    } catch (err) {
      setError(readActionError(err, t("groupManage.loadFailed")));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setToAdd([]);
      setSearch("");
      loadMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, channelId, groupJid]);

  const addPhones = useMemo(() => {
    const seen = new Set();
    const phones = [];
    for (const client of toAdd) {
      const digits = clientWhatsappPhoneDigits(client);
      if (!digits || seen.has(digits)) continue;
      seen.add(digits);
      phones.push(digits);
    }
    return phones;
  }, [toAdd]);

  const run = async (action, body) => {
    setBusy(action);
    setError("");
    try {
      const result = await apifacilGroupAdmin(channelId, action, body);
      if (result && result.error === true) {
        setError(readActionError(result, t("groupManage.actionFailed")));
        return;
      }
      if (action === "leave") {
        onClose();
        return;
      }
      setToAdd([]);
      await loadMembers({ clearError: false });
    } catch (err) {
      setError(readActionError(err, t("groupManage.actionFailed")));
    } finally {
      setBusy("");
    }
  };

  const memberLabel = (p) =>
    p?.name || formatClientDisplayPhone(p) || p?.phone || p?.lid || p?.jid || t("newRequest.noName");

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("groupManage.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Typography variant="body2" color="text.secondary">
            {t("groupManage.hint")}
          </Typography>
          <List dense>
            {loading ? (
              <ListItem>
                <ListItemText primary={t("groupManage.loading")} />
              </ListItem>
            ) : members.length ? (
              members.map((p) => {
                const token = p.jid || p.lid || p.phone;
                return (
                  <ListItem
                    key={token}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        disabled={Boolean(busy) || !token}
                        onClick={() =>
                          run("removeParticipants", { groupJid, participants: [token] })
                        }
                        aria-label={t("groupManage.remove")}
                      >
                        <PersonRemoveRoundedIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={memberLabel(p)}
                      secondary={p.phone || p.lid || p.jid || ""}
                    />
                  </ListItem>
                );
              })
            ) : (
              <ListItem>
                <ListItemText primary={t("groupManage.empty")} />
              </ListItem>
            )}
          </List>
          <WhatsappGroupParticipantsPicker
            value={toAdd}
            onChange={setToAdd}
            options={clientsData?.clients ?? []}
            loading={loadingClients}
            inputValue={search}
            onInputChange={(_, value) => setSearch(value)}
            disabled={Boolean(busy)}
            label={t("groupManage.addLabel")}
            placeholder={t("newRequest.groupParticipantsPlaceholder")}
            helperText={t("groupManage.addHint")}
            noOptionsText={
              search.trim().length < MIN_CLIENT_SEARCH_LENGTH
                ? t("newRequest.groupParticipantsPlaceholder")
                : t("newRequest.groupParticipantsEmpty")
            }
            noNameLabel={t("newRequest.noName")}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
        <Button
          color="error"
          disabled={Boolean(busy) || !groupJid}
          onClick={() => run("leave", { groupJid })}
        >
          {t("groupManage.leave")}
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={Boolean(busy)}>
            {t("newRequest.cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={Boolean(busy) || addPhones.length < 1}
            onClick={() =>
              run("addParticipants", { groupJid, participants: addPhones })
            }
          >
            {t("groupManage.add")}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
